/**
 * Llamadas HTTP del dominio Actividad de usuarios.
 *
 * SIEMPRE sobre el cliente único `lib/api` (`docs/frontend_architecture.md`,
 * Pr. 2): ningún `fetch`/`ky` suelto. El cliente ya aporta `credentials:
 * 'include'`, el `X-CSRF-TOKEN` y el `X-Sede-Id` (sede activa) en cada request, así
 * que el resumen se calcula para la sede activa sin que la feature toque la
 * cabecera a mano (el backend resuelve y valida la sede con `resolve_api_sede`).
 *
 * Los endpoints son **read-only**: el backend ya devuelve dicts JSON-safe con una
 * lista blanca de claves (`domains/user_activity.py`, `summary_to_dict`/
 * `detail_to_dict`); aquí solo se tipa la respuesta. Un `ApiError` (401/403/400 por
 * sede/5xx) lo propaga el cliente y lo gestionan los hooks/pantalla.
 *
 * Cubre la superficie de lectura de `application/api/domains/user_activity.py`
 * (#28): resumen agregado y detalle/timeline por usuario. La limpieza de duplicados
 * (`POST .../cleanup_autorefresh`) NO se expone aquí: era un artefacto del
 * auto-refresh del SSR que la SPA no provoca (no se ofrece como acción de UI).
 */
import { api } from "@/lib/api";

import type { ActivityPeriod } from "./schemas";
import type { ActivityDetail, ActivitySummary } from "./types";

/** Codifica un username para usarlo como segmento de ruta. */
function encodeSegment(value: string): string {
  return encodeURIComponent(value);
}

/**
 * Resumen agregado de actividad de la sede activa (`GET /user_activity`).
 *
 * Permiso backend: `advanced_stats` (igual que el SSR). `period` filtra el rango
 * temporal; la sede la inyecta el cliente (`X-Sede-Id`) y la valida el backend.
 */
export async function fetchActivitySummary(
  period: ActivityPeriod,
): Promise<ActivitySummary> {
  const response = await api.get("user_activity", {
    searchParams: { period },
  });
  return (await response.json()) as ActivitySummary;
}

/**
 * Detalle / timeline de la actividad de un usuario (`GET /user_activity/<username>`).
 *
 * Permiso backend: `advanced_stats`. `days` es la ventana en días hacia atrás. El
 * detalle no filtra por sede (igual que el SSR), pero el backend exige una sede
 * válida del request; la aporta el cliente.
 */
export async function fetchActivityDetail(
  username: string,
  days: number,
): Promise<ActivityDetail> {
  const response = await api.get(`user_activity/${encodeSegment(username)}`, {
    searchParams: { days },
  });
  return (await response.json()) as ActivityDetail;
}
